/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { TextProps, ThemeType } from "@packages/ui";
import { Button, FormField, Text } from "@packages/ui";
import type { KeyboardEvent } from "react";
import { Fragment, useCallback, useMemo } from "react";
import { useMediaQuery } from "usehooks-ts";

import { ScreenNames } from "../../lib/enums/screenNames";
import { Screens } from "../../lib/enums/screens";
import { nameSchema } from "../../lib/schemas/nameSchema";
import { Step, useContext } from "./Creation";

const cssDivButtonsContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
  [`@media ${Screens.Large}`]: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    paddingBottom: theme.spacing.xxl,
  },
});
const cssDivSpacer: Interpolation<ThemeType> = (theme) => ({
  height: theme.spacing.l,
  [`@media ${Screens.Large}`]: {
    flexGrow: 1,
  },
});
const cssInput: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: "transparent",
  color: theme.semanticColors.primary,
  border: "none",
  outline: "none",
});
const cssLabel: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
  paddingBottom: theme.spacing.s,
  paddingTop: theme.spacing.s,
});

export const CreationName = () => {
  const { track } = useAnalytics();
  const { form, setStep, step } = useContext();
  const isLarge = useMediaQuery(Screens.Large);
  const t = useTranslations();
  useViewedScreen({
    isReady: step === Step.Name,
    screen: ScreenNames.KidsProfileCreationName,
  });

  const name = form.watch("name");
  const isValid = useMemo(() => {
    return name.trim().length > 0 && nameSchema.safeParse({ name }).success;
  }, [name]);

  const onClick = useCallback(() => {
    if (!isValid) return;

    track({
      event: HallowAnalyticsEvent.CompletedKidsProfileCreationStep,
      properties: {
        first_name: name,
        screen_name: ScreenNames.KidsProfileCreationName,
      },
    });
    setStep(Step.Pin);
  }, [isValid, name, setStep, track]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      onClick();
    },
    [onClick],
  );

  if (step !== Step.Name) return null;

  return (
    <Fragment>
      <label css={cssLabel}>
        <Text
          {...({
            size: isLarge ? "m" : "xl",
            type: isLarge ? "headline" : "title",
          } as TextProps)}
        >
          <I18nMessage message="kids_profile_creation_name_title" />
        </Text>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <Text asChild size="xl" type="body">
              <input
                {...field}
                autoComplete="name"
                css={cssInput}
                id="name"
                onKeyDown={onKeyDown}
                placeholder={t("kids_profile_creation_name_placeholder")}
                type="text"
              />
            </Text>
          )}
        />
      </label>
      <div css={cssDivSpacer} />
      <div css={cssDivButtonsContainer}>
        <Button
          disabled={!isValid}
          isFullWidth={!isLarge}
          onClick={onClick}
          size="l"
          type="button"
        >
          <I18nMessage message="onboarding_button_continue" />
        </Button>
      </div>
    </Fragment>
  );
};
